import { Fragment } from 'react';

import { Listbox, Transition } from '@headlessui/react';
import { IconType } from 'react-icons';
import { BiCheck } from 'react-icons/bi';
import { HiFilter } from 'react-icons/hi';

import { SelectOption } from './Select';
import Text from './Text';

interface Props {
  onChange: any;
  displayLabel: string;
  options?: SelectOption[] | any;
  values: string[] | any;
  icon?: IconType;
}

const MultiSelectCheckbox: React.FC<Props> = ({
  options,
  onChange,
  displayLabel,
  icon: Icon,
  values,
}) => {
  const hasOption = values ? values!.length > 0 : false;

  return (
    <Listbox value={values} onChange={(_v) => {}} multiple>
      <div className="relative w-full">
        {/* Layer on top of the button - matches CriteriaSelect structure */}
        <div className="absolute inset-0 flex gap-2 pl-[1.1rem] pointer-events-none items-center">
          {/* Icon spot */}
          <span
            className={`flex ${
              hasOption ? 'text-gray-600' : 'text-gray-600/70'
            }`}
          >
            {Icon ? <Icon size={20} /> : <HiFilter size={20} />}
          </span>
          {/* Label */}
          <span>
            <Text
              as="p"
              text={displayLabel}
              className={`text-gray-600 transition-all ${
                hasOption
                  ? 'transfrom -translate-y-2 text-3xs'
                  : 'text-xs md:text-sm'
              }`}
            />
          </span>
        </div>

        <Listbox.Button
          className={`select pl-11 pt-3 pb-3 w-full rounded-md focus:outline-none border-[1px] border-gray-300 text-xs md:text-sm font-bold focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 ${
            hasOption ? 'text-primary bg-gray-200/70' : 'text-gray-700'
          } flex justify-between items-center pointer-events-auto text-left min-h-[48px]`}
        >
          {/* Selected values display - positioned to match CriteriaSelect value positioning */}
          <span className={`flex-1 ${hasOption ? 'mt-3' : ''}`}>
            {values && values.length > 0 && (
              <Text
                as="p"
                text={
                  values.length > 1
                    ? `${values.slice(0, 1).join(', ')}...`
                    : values.length === 1
                    ? values[0]
                    : values.join(', ')
                }
                className="text-primary font-bold text-xs md:text-sm truncate"
              />
            )}
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-[9999] mt-2 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {options.map((option: any) => (
              <Listbox.Option
                key={option.value}
                className={({ active }) =>
                  `relative z-[9999] cursor-pointer select-none py-2 pl-10 pr-4 ${
                    active ? 'bg-gray-100 text-primary' : 'text-gray-600'
                  }`
                }
                value={option.label}
                onClick={() => onChange(option.label)}
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block truncate ${
                        selected ? 'font-medium' : 'font-normal'
                      }`}
                    >
                      {option.display}
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                        <BiCheck className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};

export default MultiSelectCheckbox;
